/**
 * "Apply this preset to the customers already on it" (D1b, §2.5).
 *
 * ── Why a preview and not a button ──────────────────────────────────────
 *
 * A preset edit reaches nobody by default — that is D1, and it is right, because
 * the alternative silently rewrites every private drive on the deployment from a
 * screen opened to re-price a paid tier. But an operator who raises the included
 * budget usually DOES mean "and give it to everyone on that plan", and doing that
 * customer by customer through the Limits tab would mark each of them CUSTOM:
 * exactly backwards, since they are standard.
 *
 * So this is the deliberate act, and the preview is what makes it safe. It is
 * also the only safety there is — **there is no propagation undo**. Every write
 * lands an audited row, so the previous values are recoverable by reading the
 * log, but nothing reverts them in bulk. Stated in the dialog rather than
 * discovered afterwards.
 *
 * ── Three things the copy has to get right ──────────────────────────────
 *
 *  1. The first bucket says **"not hand-edited"**, never "still matches the
 *     preset". The flag does not claim that (§2.3): a preset can move without a
 *     tenant being propagated, and that tenant reads "Default" while differing.
 *  2. Custom customers are **listed but unticked**. Overwriting a deliberate
 *     customisation takes the extra click, and seeing the names is what makes
 *     that click informed — a hidden bucket would be a silent exclusion.
 *  3. **Losing model access is called out per customer.** It is the one
 *     propagation effect a customer notices immediately, and under manual
 *     authority it revokes silently, with no upsell to explain the absence.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { Modal, useToast, useBillingCurrency } from '@papan/core-ui/ui'
import { formatMoney } from '@papan/types'
import { budgetApi, type PresetApplicableResponse, type PresetApplicableRow } from '../../client/budget'
import { pinnedBatch } from '@papan/api-client'
import { formatBytes } from './bits'

export function PropagatePresetDialog({
  planId,
  planName,
  onClose,
  mode = 'reset',
}: {
  planId: string
  planName: string
  onClose: () => void
  /**
   * 'reset' (default) — the historical "apply preset": clear the customers'
   * ceiling overrides and re-project, so they match the preset. Custom customers
   * start unticked.
   * 'propagate' — auto-apply (F3): push the plan's new values to inheritors while
   * PRESERVING any explicitly-overwritten axis. Every tenant on the plan (custom
   * included) starts ticked, because a custom tenant still picks up the plan's
   * non-overwritten values and keeps the rest.
   */
  mode?: 'reset' | 'propagate'
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()

  const [data, setData] = useState<PresetApplicableResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Ticked ids. In 'reset' seeded from `standard` only; in 'propagate' from both,
   *  since overrides are preserved. */
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await budgetApi.getPresetApplicable(planId)
      setData(res)
      setSelected(new Set(
        mode === 'propagate'
          ? [...res.standard, ...res.custom].map((r) => r.tenantId)
          : res.standard.map((r) => r.tenantId),
      ))
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Could not work out who this would affect')
    }
  }, [planId, mode])

  useEffect(() => { void load() }, [load])

  const toggle = (id: string) => setSelected((cur) => {
    const next = new Set(cur)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const run = async () => {
    const ids = [...selected]
    if (ids.length === 0) return
    setRunning(true)
    setProgress({ done: 0, total: ids.length })
    let queue = ids
    let done = 0
    const failures: Array<{ tenantId: string; error: string }> = []
    // #1316 review round 2 (owner, 2026-10-28): the propagation the operator started FINISHES as the identity that
    // started it — every batch call goes as it; nothing is shown once that session ended
    const batch = pinnedBatch()
    try {
      // Loops until the server reports nothing remaining. The BATCH is the
      // server's choice, not this component's — a client that picked its own
      // page size could out-run a bound the route relies on.
      while (queue.length > 0) {
        const r = await batch.run(() => budgetApi.applyPresetToTenants(planId, queue, mode))
        done += r.applied.length
        failures.push(...r.failed)
        if (!batch.ended()) setProgress({ done, total: ids.length })
        queue = r.remaining
      }
      if (batch.ended()) return
      if (failures.length > 0) {
        toast.error(t('settings.platform.propagate.partial', {
          defaultValue: 'Applied to {{done}}; {{failed}} could not be updated ({{first}}).',
          done, failed: failures.length,
          first: `${failures[0].tenantId}: ${failures[0].error}`,
        }))
      } else {
        toast.success(t('settings.platform.propagate.done', {
          defaultValue: 'Applied {{plan}} to {{count}} tenant.',
          defaultValue_other: 'Applied {{plan}} to {{count}} tenants.',
          plan: planName, count: done,
        }))
      }
      onClose()
    } catch (e: any) {
      // Partial progress is REPORTED, not hidden: the operator needs to know how
      // far it got before re-running, because re-running is safe but pretending
      // nothing happened is not.
      if (batch.ended()) return
      toast.error(t('settings.platform.propagate.failed', {
        defaultValue: 'Stopped after {{done}} of {{total}}: {{err}}',
        done, total: ids.length, err: e?.message || 'unknown error',
      }))
      setRunning(false)
    }
  }

  const losing = data
    ? [...data.standard, ...data.custom].filter(
        (r) => selected.has(r.tenantId) && r.losingModels.length > 0,
      )
    : []

  return (
    // `dismissible={running ? false : true}` — mid-apply this dialog must not
    // vanish to an Escape or a stray backdrop click: the writes are per-tenant
    // and there is no undo, so an operator who loses the progress line cannot
    // tell how far it got.
    <Modal
      onClose={onClose}
      dismissible={!running}
      title={mode === 'propagate'
        ? t('settings.platform.propagate.propagateTitle', {
            defaultValue: 'Apply "{{plan}}" changes to its tenants?', plan: planName,
          })
        : t('settings.platform.propagate.title', {
            defaultValue: 'Apply "{{plan}}" to existing tenants?', plan: planName,
          })}
      description={mode === 'propagate'
        ? t('settings.platform.propagate.propagateDesc', {
            defaultValue:
              "Pushes this plan's new values to the tenants on it. Any value a tenant has explicitly overwritten is kept — only inherited values change. There is no undo, but every change is in the audit log.",
          })
        : t('settings.platform.propagate.desc', {
            defaultValue:
              'Copies this preset\'s ceilings and model list onto the tenants you pick. It does not change anyone you leave unticked, and there is no undo — every change is in the audit log, but nothing reverts them in bulk.',
          })}
      footer={
        <>
          <span className="themed-text-muted text-[11px] mr-auto">
            {progress
              ? t('settings.platform.propagate.progress', {
                  defaultValue: '{{done}} of {{total}}…', done: progress.done, total: progress.total,
                })
              : ''}
          </span>
          <button
            onClick={onClose}
            disabled={running}
            className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-50"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={run}
            disabled={running || selected.size === 0}
            className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
          >
            {t('settings.platform.propagate.apply', {
              defaultValue: 'Apply to {{count}}', count: selected.size,
            })}
          </button>
        </>
      }
    >
        <div className="px-5 py-4 space-y-4">
          {error && <p className="text-sm text-red-400">{error}</p>}
          {!data && !error && (
            <p className="themed-text-muted text-sm">{t('common:loading')}</p>
          )}

          {data && data.standard.length === 0 && data.custom.length === 0 && (
            <p className="themed-text-secondary text-sm">
              {t('settings.platform.propagate.none', {
                defaultValue:
                  'No tenant is on this preset, so there is nothing to apply. New tenants will be seeded from it as normal.',
              })}
            </p>
          )}

          {data && data.standard.length > 0 && (
            <Bucket
              // "not hand-edited", NOT "still matches the preset" — see the header.
              title={t('settings.platform.propagate.standard', {
                defaultValue: '{{count}} tenant not hand-edited',
                defaultValue_other: '{{count}} tenants not hand-edited',
                count: data.standard.length,
              })}
              rows={data.standard}
              selected={selected}
              disabled={running}
              onToggle={toggle}
            />
          )}

          {data && data.custom.length > 0 && (
            <Bucket
              title={t('settings.platform.propagate.custom', {
                defaultValue: '{{count}} tenant with a Custom plan',
                defaultValue_other: '{{count}} tenants with Custom plans',
                count: data.custom.length,
              })}
              note={mode === 'propagate'
                ? t('settings.platform.propagate.customNotePropagate', {
                    defaultValue:
                      'Hand-edited at least once. Applying keeps every value they overwrote and only updates the ones they still inherit — so they stay Custom.',
                  })
                : t('settings.platform.propagate.customNote', {
                    defaultValue:
                      'Hand-edited at least once, so they are left out by default. Ticking one overwrites what was set for them, and clears the Custom mark — they will match the preset afterwards.',
                  })}
              warn
              rows={data.custom}
              selected={selected}
              disabled={running}
              onToggle={toggle}
            />
          )}

          {/* The effect a customer feels the same day, so it is not buried in a
              per-row detail. */}
          {losing.length > 0 && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2">
              <p className="text-amber-200 text-xs font-medium">
                {t('settings.platform.propagate.losingTitle', {
                  defaultValue: 'This takes AI models away from {{count}} of them',
                  count: losing.length,
                })}
              </p>
              <ul className="text-amber-200/80 text-[11px] mt-1 space-y-0.5">
                {losing.slice(0, 6).map((r) => (
                  <li key={r.tenantId}>{r.name} — {r.losingModels.join(', ')}</li>
                ))}
              </ul>
              <p className="text-amber-200/70 text-[11px] mt-1">
                {t('settings.platform.propagate.losingNote', {
                  defaultValue:
                    'Their picker stops offering these immediately, and a running conversation on one will be refused.',
                })}
              </p>
            </div>
          )}
        </div>
    </Modal>
  )
}

function Bucket({ title, note, warn, rows, selected, disabled, onToggle }: {
  title: string
  note?: string
  warn?: boolean
  rows: PresetApplicableRow[]
  selected: Set<string>
  disabled: boolean
  onToggle: (id: string) => void
}) {
  const billingCurrency = useBillingCurrency()
  return (
    <div>
      <div className={`text-xs font-medium mb-1 ${warn ? 'text-amber-300' : 'themed-text'}`}>
        {title}
      </div>
      {note && <p className="themed-text-muted text-[11px] mb-1.5">{note}</p>}
      <div className="themed-bg-primary rounded border themed-border max-h-48 overflow-y-auto">
        {rows.map((r) => (
          <label
            key={r.tenantId}
            className="flex items-center gap-2 px-2.5 py-1.5 border-b themed-border last:border-b-0 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selected.has(r.tenantId)}
              disabled={disabled}
              onChange={() => onToggle(r.tenantId)}
            />
            <span className="min-w-0 flex-1">
              <span className="themed-text text-xs block truncate">{r.name}</span>
              {/* WHAT would change, not only WHO (F-37).
                  ────────────────────────────────────────────────────────
                  The dialog's own copy says "there is no undo", and until now
                  it named the tenants and nothing about the numbers — so the
                  operator confirming a bulk ceiling rewrite could not see that
                  one of the rows was about to have its budget halved.

                  Only fields that DIFFER are sent, so a tenant already matching
                  the preset stays a single quiet line and the one that moves is
                  the one that draws the eye. */}
              {r.changes.length > 0 && (
                <span className="themed-text-muted text-[10px] block truncate">
                  {r.changes.map(c => `${capLabel(c.field)} ${capValue(c.field, c.from, billingCurrency)} → ${capValue(c.field, c.to, billingCurrency)}`).join(' · ')}
                </span>
              )}
            </span>
            <span className="themed-text-muted text-[10px] whitespace-nowrap shrink-0">{r.planLabel}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

/**
 * Short names — the row is one line and the full labels do not fit.
 *
 * The `never` guard is not decoration. Without it an unmatched field returns
 * `undefined`, React renders that as the string "undefined", and it does so as
 * the LABEL of a ceiling on the confirmation for a bulk write with no undo —
 * which is what shipped when the server renamed `includedBudgetUsd` to
 * `includedBudgetBillingMicro` and this switch was not updated. Falling off the
 * end must be a COMPILE error, because it is invisible at runtime.
 */
function capLabel(field: PresetApplicableRow['changes'][number]['field']): string {
  switch (field) {
    case 'includedBudgetBillingMicro': return 'Included'
    case 'extraCapBillingMicro': return 'Overage'
    case 'storageQuotaBytes': return 'Storage'
    case 'agentConcurrencyCap': return 'Agents'
    default: {
      const _exhaustive: never = field
      return _exhaustive
    }
  }
}

/**
 * `null` is a REAL value on every one of these axes and never means "unset":
 * unlimited for a money cap, the platform default for concurrency. Rendering it
 * as a dash — or worse, as 0 — would state the opposite of the truth on the one
 * screen that exists to show what is about to be overwritten.
 */
function capValue(
  field: PresetApplicableRow['changes'][number]['field'],
  v: number | null,
  currency: string,
): string {
  if (field === 'agentConcurrencyCap') return v === null ? 'default' : String(v)
  if (v === null) return 'unlimited'
  if (field === 'storageQuotaBytes') return formatBytes(v)
  // Everything left is a MONEY axis. Named exhaustively rather than left as a
  // fall-through: this function survived the same rename `capLabel` did not,
  // purely because the money branch was the default — correct by luck, which is
  // not a property worth keeping on a screen about to overwrite ceilings.
  // A new NON-money axis added to the union would otherwise be silently
  // formatted as currency here; now it fails to compile.
  switch (field) {
    case 'includedBudgetBillingMicro':
    case 'extraCapBillingMicro':
      // BILLING-currency MICRO units, verified against the stored rows —
      // `200000000` is 200 major units, not two hundred million. `formatMoney`
      // owns that conversion; a hand-rolled one here would be the whitelabel
      // bug this repo documents at length. The currency comes from the caller
      // because this is a plain function, not a component, and so cannot read
      // the hook itself.
      return formatMoney(v, { currency })
    default: {
      const _exhaustive: never = field
      return _exhaustive
    }
  }
}
